<template>
  <div id="options">
    <v-container fluid>
      <v-flex xs12>
        <HowToUseThisPage />
        <div class="option-container">
          <div class="route-container mb-4 section-heading">
            <h1>
              <v-icon class="option-icon mr-2" :color="iconColor">
                mdi-cogs
              </v-icon>
              MANAGE SETTINGS
            </h1>
          </div>
          <v-flex class="d-flex align-center mb-7 mt-12">
            <div v-if="checkAuthorization(['owner'])" class="mr-6">
              <form ref="settingForm" @submit.prevent="addSetting">
                <v-card class="py-10 px-5" width="300" max-width="400">
                  <v-text-field
                    v-model="newSetting.key"
                    label="Setting Key"
                    variant="outlined"
                    class="mt-1"
                    density="compact"
                    color="#A0C548"
                    style="min-width: 100%"
                  ></v-text-field>
                  <v-text-field
                    v-model="newSetting.value"
                    label="Setting Value"
                    variant="outlined"
                    class="mt-1"
                    density="compact"
                    color="#A0C548"
                    style="min-width: 100%"
                  ></v-text-field>
                  <v-btn
                    :disabled="!isValidNewSetting"
                    color="rgba(165, 216, 103, 1)"
                    class="elevation-0 font-weight-bold add-setting-btn px-8"
                    type="submit"
                  >
                    Add Setting
                  </v-btn>
                </v-card>
              </form>
            </div>
          </v-flex>
          <!-- Table to display existing settings -->
          <v-data-table
            :headers="headers"
            :items="settings"
            :items-per-page="10"
            fixed-header
            class="elevation-1 churches-table"
            :loading="isloading"
            loading-text="Loading Settings... Please wait"
            no-data-text="No Settings found"
            :hide-default-footer="true"
          >
            <template #headers>
              <tr>
                <th class="text-left">Key</th>
                <th class="text-left">Value</th>
              </tr>
            </template>

            <!-- Optional: Show simple table rows -->
            <template #item="{ item }">
              <tr>
                <td>{{ item.key }}</td>
                <td>{{ item.value }}</td>
              </tr>
            </template>

            <!-- Optional: Loading progress bar -->
            <template #progress>
              <v-progress-linear
                color="#592892"
                indeterminate
              ></v-progress-linear>
            </template>
          </v-data-table>
        </div>
      </v-flex>
    </v-container>
  </div>
</template>

<script>
  // import firebase from 'firebase/app';
  import 'firebase/firestore'
  import db from '@/firebase/init'
  import HowToUseThisPage from '@/components/utilities/HowToUseThisPage'
  import { confirmAction } from '@/components/utilities/SwalService'
  import Swal from 'sweetalert2'
  import { currentTheme } from '@/plugins/vuetify'
  import { matchesConfirmWord } from '@/helpers/confirmWord'

  export default {
    data() {
      return {
        settings: [],
        newSetting: { key: '', value: '' },
        isEditing: false,
        singleExpand: true,
        isloading: true,
        headers: [
          { text: 'Key', default: true, value: 'key' },
          { text: 'Value', default: true, value: 'value' },
          // { text: 'Actions', sortable: false, value: 'action', align: 'right' },
        ],
      }
    },
    components: {
      HowToUseThisPage,
    },
    created() {
      this.fetchSettingsOnce() // Ensure settings are fetched when component is created
    },
    computed: {
      iconColor() {
        return currentTheme.value === 'dark' ? '#cecece' : '#383935'
      },
      keyRules() {
        return [(v) => !!v || 'Key is required']
      },
      valueRules() {
        return [(v) => !!v || 'Value is required']
      },
      isValidNewSetting() {
        return this.newSetting.key && this.newSetting.value
      },
    },
    methods: {
      checkAuthorization(permissions) {
        const userRoles = this.$store.getters['UserData/getRoles']
        if (permissions && userRoles) {
          const normalizedPermissions = permissions.includes('superAdmin')
            ? [...permissions, 'helpDesk']
            : permissions

          const hasRole = normalizedPermissions.filter(() =>
            userRoles.some((role) => normalizedPermissions.includes(role))
          )
          if (hasRole && hasRole.length > 0) return true
        }
        return false
      },
      isHelpDesk() {
        const userRoles = this.$store.getters['UserData/getRoles']
        return Array.isArray(userRoles) && userRoles.includes('helpDesk')
      },
      fetchSettingsOnce() {
        this.isloading = true
        this.newSetting.key = ''
        this.newSetting.value = ''

        setTimeout(() => {
          db.collection('settings')
            .get()
            .then((snapshot) => {
              this.settings = snapshot.docs.map((doc) => ({
                key: doc.id,
                value: doc.data().value,
              }))
              this.isloading = false
            })
            .catch((error) => {
              console.error('Error fetching settings:', error)
            })
        }, 1000)
      },
      addSetting() {
        const newSettingRef = db.collection('settings').doc(this.newSetting.key)
        newSettingRef.set({ value: this.newSetting.value }).then(() => {
          // Optionally refresh the list after adding
          this.fetchSettingsOnce()
        })
      },
      async deleteSetting(item) {
        if (this.isHelpDesk && this.isHelpDesk()) {
          const key = 'options.messages.deleteNotAllowed'
          this.feedback =
            (this.$te && this.$te(key) ? this.$t(key) : null) ||
            'Help Desk users cannot remove settings.'
          return
        }

        if (!item || !item.key) {
          Swal.fire({
            title: 'Houston we have a problem.',
            text: 'Error with the delete function, missing params.',
            icon: 'error',
            confirmButtonClass:
              'v-btn v-btn--contained theme--dark v-size--default v-btn--depressed',
          })
          return
        }

        const confirmed = await confirmAction({
          title: `You are about to remove: <br>"${item.key}"`,
          html: 'Type DELETE below to confirm.',
          input: 'text',
          inputPlaceholder: 'Type DELETE',
          inputValidator: (value) => {
            return new Promise((resolve) => {
              if (matchesConfirmWord(value, 'DELETE')) {
                resolve()
              } else {
                resolve('You need to type DELETE to confirm')
              }
            })
          },
          confirmButtonText: 'Yes Remove it!',
          cancelButtonText: 'No, Keep it!',
          context: this,
        })

        if (!confirmed) return

        try {
          await db.collection('settings').doc(item.key).delete()
          this.fetchSettingsOnce()
        } catch (error) {
          console.error('Error removing document:', error)
        }
      },
    },
  }
</script>

<style scoped>
  .option-container {
    margin-top: 50px;
    margin-bottom: 25%;
  }

  .option-container .option-icon {
    margin-right: 10px;
    color: #000;
    width: 30px;
  }
  .add-setting-btn {
    background-color: #a5d867;
    color: black;
    font-weight: bold;
    border-radius: 8px;
    text-transform: uppercase;
  }

  .add-setting-btn:disabled {
    background-color: #e0e0e0 !important;
    color: #9e9e9e !important;
    cursor: not-allowed;
  }
</style>
